import { z } from 'zod';

// Strict schema for packages/service-*/service.yaml. Unknown keys are
// rejected so typos surface at build time, not at runtime.

const SLUG = z.string().regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, 'kebab-case slug required');
const KEBAB_KEY = z.string().regex(/^[a-z][a-z0-9-]*$/, 'lowercase kebab id required');

export const NavPlacementSchema = z
  .object({
    group: z.string().min(1, 'nav.group required (e.g. "Identity & Access")'),
    label: z.string().min(1, 'nav.label required'),
    order: z.number().int().nonnegative().default(0),
    icon: z.string().optional(),
  })
  .strict();

export const ResourceDefSchema = z
  .object({
    name: z.string().min(1),
    pattern: z.string().optional(),
    detail: z
      .object({ pattern: z.string().optional(), tabs: z.array(z.string()).optional() })
      .partial()
      .strict()
      .optional(),
    create: z
      .object({
        pattern: z.string().optional(),
        steps: z.array(z.string()).optional(),
        enforces: z.array(z.string()).optional(),
      })
      .partial()
      .strict()
      .optional(),
    edit: z.object({ pattern: z.string().optional() }).partial().strict().optional(),
    list: z
      .object({ columns: z.array(z.string()).optional(), defaultSort: z.string().optional() })
      .partial()
      .strict()
      .optional(),
  })
  .strict();

export const ToolDefSchema = z
  .object({
    id: KEBAB_KEY,
    pattern: z.string().optional(),
    label: z.string(),
    steps: z.array(z.string()).optional(),
  })
  .strict();

export const ExportDefSchema = z
  .object({
    id: z.string().min(1),
    purpose: z.string().min(1),
  })
  .strict();

export const ConstraintSchema = z
  .object({
    description: z.string().min(1),
    rules: z.array(z.string()).optional(),
  })
  .strict();

export const SmithyDefSchema = z
  .object({
    source: z.string().min(1, 'smithy.source path required (relative to VEX_SOURCE_ROOT)'),
    client: z.string().min(1, 'npm package name for the generated client'),
  })
  .strict();

export const ServiceManifestSchema = z
  .object({
    apiVersion: z.literal('vex.vextura.ai/console-section/v1'),
    service: SLUG,
    owner_team: SLUG,
    description: z.string().optional(),
    nav: NavPlacementSchema,
    smithy: SmithyDefSchema.optional(),
    resources: z.array(ResourceDefSchema).default([]),
    tools: z.array(ToolDefSchema).default([]),
    exports: z.array(ExportDefSchema).default([]),
    consumes: z.array(z.string()).default([]),
    constraints: z.array(ConstraintSchema).default([]),
  })
  .strict();

export type ServiceManifest = z.infer<typeof ServiceManifestSchema>;
export type NavPlacement = z.infer<typeof NavPlacementSchema>;
export type ResourceDef = z.infer<typeof ResourceDefSchema>;
export type ToolDef = z.infer<typeof ToolDefSchema>;
export type ExportDef = z.infer<typeof ExportDefSchema>;
export type Constraint = z.infer<typeof ConstraintSchema>;
